import { ReactElement } from 'react';
import { Tooltip } from 'antd';
import { BulkCreateCustomersResult } from '@minutes-system/backend/src/api/customers/schema';
import { SuccessIcon } from '../../../../../components/SuccessIcon';
import { ErrorIcon } from '../../../../../components/ErrorIcon';
import { WarningIcon } from '../../../../../components/WarningIcon';

interface Props {
  result?: BulkCreateCustomersResult;
}

const getReasonText = (reason: string): string => {
  const reasonMap: Record<string, string> = {
    // 警告
    'phone-shared': '固定電話番号が既存の顧客と共有されています(夫婦・家族の可能性)',
    'same-name-exists': '同姓同名の顧客が既に存在します',
    // エラー
    'exact-duplicate': '完全に同一のレコードが既に存在します',
    'same-person-update-needed': '既存レコードの更新が必要です(同一人物)',
    'name-changed': '氏名変更の可能性があります',
    'mobile-duplicate': '携帯電話番号が既に存在します(削除忘れの可能性)',
    'other-error': 'エラーが発生しました',
  };
  return reasonMap[reason] ?? reason;
};

const getTooltipContent = (result: BulkCreateCustomersResult): string | null => {
  switch (result.resultType) {
    case 'success':
      return null;
    case 'success-with-warning': {
      const reasonText = getReasonText(result.reason);
      const existingInfo = result.existingRecords
        .map(r => `${r.name} (固定電話: ${r.phone ?? 'なし'}, 携帯電話: ${r.mobilePhone ?? 'なし'})`)
        .join(', ');
      return `${reasonText}\n既存レコード: ${existingInfo}`;
    }
    case 'error-input-duplicate': {
      const rowNumbers = result.duplicateRows.map(r => `${String(r.rowNumber)}行目`).join(', ');
      return `${rowNumbers}と重複しています`;
    }
    case 'error-existing-duplicate': {
      const reasonText = getReasonText(result.reason);
      const existingInfo = result.existingRecords
        .map(r => `${r.name} (固定電話: ${r.phone ?? 'なし'}, 携帯電話: ${r.mobilePhone ?? 'なし'})`)
        .join(', ');
      return `${reasonText}\n既存レコード: ${existingInfo}`;
    }
  }
};

const ResultIcon = ({ result }: Props): ReactElement | null => {
  if (result === undefined) {
    return null;
  }

  switch (result.resultType) {
    case 'success': {
      return <SuccessIcon />;
    }
    case 'success-with-warning': {
      return (
        <Tooltip title={getTooltipContent(result)} placement='left'>
          <WarningIcon />
        </Tooltip>
      );
    }
    case 'error-input-duplicate':
    case 'error-existing-duplicate': {
      return (
        <Tooltip title={getTooltipContent(result)} placement='left'>
          <ErrorIcon />
        </Tooltip>
      );
    }
  }
};

export default ResultIcon;
